import * as React from 'react';
import cx from 'classnames';

import { HeaderLogo } from '../HeaderLogo/HeaderLogo';
import { ContactUsButton } from '../ContactUsButton/ContactUsButton';
import { Clickable } from '../../../../components/Clickable/Clickable';
import { routes } from '../../../../config/routes';
import { useMedia, DESKTOP_MENU_QUERY } from '../../../../utils/useMedia';
import { MainNavigation } from '../MainNavigation/MainNavigation';

import * as cs from './StickyMobileHeader.module.css';

interface Props {
  mainPage?: boolean;
  withPageBg?: boolean;
  withBgImage?: boolean;
  isBgFullSize?: boolean | null;
  themeVariables?: React.CSSProperties;
  innerRef?: React.Ref<HTMLDivElement>;
  hideContactUs: boolean;
}

export const StickyMobileHeader = ({
  mainPage,
  withPageBg,
  withBgImage,
  isBgFullSize,
  themeVariables,
  innerRef,
  hideContactUs,
}: Props) => {
  const [sticked, setSticked] = React.useState(false);
  const headerRef = React.useRef(null);
  const isDesktop = useMedia(DESKTOP_MENU_QUERY);

  React.useEffect(() => {
    if (isDesktop || !('IntersectionObserver' in window)) {
      return;
    }

    const onIntersect: IntersectionObserverCallback = (entries) => {
      const [entry] = entries;
      setSticked(entry.intersectionRatio < 1);
    };

    const observer = new IntersectionObserver(onIntersect, {
      root: null,
      rootMargin: '0px',
      threshold: [1],
    });

    const node = headerRef && headerRef.current;

    if (node && observer) {
      observer.observe(node);
    }

    return () => {
      if (observer) {
        if (node) {
          observer.unobserve(node);
        }
        observer.disconnect();
      }
    };
  }, [isDesktop]);

  return (
    <header
      className={cx(cs.headerMobile, {
        [cs.sticked]: sticked,
        [cs.withPageBg]: withPageBg,
        [cs.withBgImage]: withBgImage,
        [cs.isBgFullSize]: isBgFullSize,
      })}
      ref={headerRef}
      data-with-theme={!sticked}
      data-theme-reset={sticked}
      style={sticked ? {} : themeVariables}
      id="sticky-mobile-header"
    >
      <div ref={innerRef} className={cs.content}>
        <div className={cs.headerTop}>
          <Clickable
            className={cs.home}
            to={mainPage ? '#' : routes.home}
            aria-label="Home"
            trackEvent="primary-navigation-home"
          >
            <HeaderLogo />
          </Clickable>

          {!hideContactUs && <ContactUsButton />}
        </div>

        <MainNavigation className={cs.nav} />
      </div>
    </header>
  );
};

StickyMobileHeader.displayName = 'StickyMobileHeader';
